import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import '../styles/CommunityConnections.css';

interface Connection {
  id: string;
  senderId: string;
  receiverId: string;
  status: string;
  message: string | null;
  createdAt: string;
  sender: {
    id: string;
    email: string;
    profile?: {
      displayName: string | null;
      headline: string | null;
      avatarUrl: string | null;
      location: string | null;
    };
  };
  receiver: {
    id: string;
    email: string;
    profile?: {
      displayName: string | null;
      headline: string | null;
      avatarUrl: string | null;
      location: string | null;
    };
  };
}

const CommunityConnections: React.FC = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'accepted' | 'pending' | 'sent'>('accepted');

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/auth/community/connections');
      setConnections(response.data.connections || []);
    } catch (err) {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const respondToConnection = async (connectionId: string, status: 'accepted' | 'rejected') => {
    try {
      await axiosInstance.put(`/auth/community/connections/${connectionId}`, { status });
      toast.success(status === 'accepted' ? 'Connection accepted!' : 'Connection declined');
      fetchConnections();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to respond');
    }
  };

  // We need to figure out current user to determine which connections are "received" vs "sent"
  // We'll use a simple approach: pending where user is receiver = "pending", pending where user is sender = "sent"
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await axiosInstance.get('/auth/profile');
        setCurrentUserId(res.data.user?.id || null);
      } catch {
        // ignore
      }
    };
    fetchMe();
  }, []);

  const accepted = connections.filter(c => c.status === 'accepted');
  const pending = connections.filter(c => c.status === 'pending' && c.receiverId === currentUserId);
  const sent = connections.filter(c => c.status === 'pending' && c.senderId === currentUserId);

  const getOtherUser = (c: Connection) => {
    if (c.senderId === currentUserId) return c.receiver;
    return c.sender;
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const tabCounts = {
    accepted: accepted.length,
    pending: pending.length,
    sent: sent.length,
  };

  const currentList = tab === 'accepted' ? accepted : tab === 'pending' ? pending : sent;

  return (
    <div className="cc-container">
      <div className="cc-header">
        <button className="cc-back-btn" onClick={() => navigate('/community')}>
          ← Back to Community
        </button>
        <h1>My Connections</h1>
        <p>Manage your professional network</p>
      </div>

      <div className="cc-nav">
        <div className="cc-tabs">
          {(['accepted', 'pending', 'sent'] as const).map(t => (
            <button
              key={t}
              className={`cc-tab ${tab === t ? 'active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'accepted' ? '✅ Connected' : t === 'pending' ? '📩 Pending' : '📤 Sent'}
              {tabCounts[t] > 0 && <span className="cc-badge">{tabCounts[t]}</span>}
            </button>
          ))}
        </div>
        <button className="cc-find-btn" onClick={() => navigate('/community/members')}>
          🔍 Find People
        </button>
      </div>

      {loading ? (
        <div className="cc-loading">Loading connections...</div>
      ) : currentList.length === 0 ? (
        <div className="cc-empty">
          {tab === 'accepted' && <p>You don't have any connections yet. <button onClick={() => navigate('/community/members')}>Browse Members</button></p>}
          {tab === 'pending' && <p>No pending requests.</p>}
          {tab === 'sent' && <p>No sent requests awaiting response.</p>}
        </div>
      ) : (
        <div className="cc-list">
          {currentList.map(c => {
            const other = getOtherUser(c);
            const profile = other.profile;
            return (
              <div key={c.id} className="cc-card">
                <div className="cc-card-avatar">
                  {profile?.avatarUrl ? (
                    <img src={profile.avatarUrl} alt={profile.displayName || ''} />
                  ) : (
                    <span className="cc-initials">{getInitials(profile?.displayName)}</span>
                  )}
                </div>
                <div className="cc-card-info">
                  <h3>{profile?.displayName || other.email}</h3>
                  {profile?.headline && <p className="cc-headline">{profile.headline}</p>}
                  {profile?.location && <span className="cc-location">📍 {profile.location}</span>}
                  <span className="cc-date">
                    {tab === 'accepted' ? 'Connected' : tab === 'pending' ? 'Received' : 'Sent'} {formatDate(c.createdAt)}
                  </span>
                  {c.message && <p className="cc-message">"{c.message}"</p>}
                </div>
                <div className="cc-card-actions">
                  {tab === 'pending' && (
                    <>
                      <button className="cc-accept-btn" onClick={() => respondToConnection(c.id, 'accepted')}>
                        ✓ Accept
                      </button>
                      <button className="cc-reject-btn" onClick={() => respondToConnection(c.id, 'rejected')}>
                        ✗ Decline
                      </button>
                    </>
                  )}
                  {tab === 'accepted' && (
                    <span className="cc-status-connected">🤝 Connected</span>
                  )}
                  {tab === 'sent' && (
                    <span className="cc-status-pending">⏳ Awaiting</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CommunityConnections;
